<template>
  <div class="category-top-container">
    <div class="category-top">
      <div class="navigation">
        <SfHeader :title="pageTitleGtm ? pageTitleGtm : ''">
          <template #navigation>
            <SfHeaderNavigationItem
              v-for="(category, index) in menu.items"
              :key="index"
              ref="navItem"
              v-e2e="'app-header-url_women'"
              class="nav-item"
              :label="category.label"
              :link="localePath(category.link)"
            />
          </template>
        </SfHeader>
      </div>
      <template v-if="$fetchState.pending">
        <content-placeholders>
          <div class="first">
            <content-placeholders-img />
          </div>
        </content-placeholders>
      </template>
      <template v-else>
        <div class="breadcrumb-container">
          <div
            v-if="breadcrumbs"
            class="breadcrumb"
          >
            <span
              class="breadcrumb-title"
              v-text="breadcrumbs"
            />
          </div>
          <div class="category-filter">
            <CategoryFilter
              :category-id="categoryId"
              :search-specs-str="searchSpecsStr"
            />
          </div>
        </div>
      </template>
    </div>
    <PageTrackingGtm
      v-if="pageTitleGtm !== undefined"
      page-type-gtm="category"
      :page-title-gtm="pageTitleGtm"
    />
  </div>
</template>

<script>
import { SfHeader } from '@storefront-ui/vue';
import {
  ref,
  defineComponent,
  useFetch,
  computed,
} from '@nuxtjs/composition-api';
import { useCategorySearch, useMenu } from '@vue-storefront/gemini';
import PageTrackingGtm from '~/components/Gtm/PageTrackingGtm.vue';
import CategoryFilter from '~/components/Category/CategoryFilter.vue';
import { useKlaviyo } from '~/composables';

export default defineComponent({
  name: 'CategoryTree',
  components: {
    CategoryFilter,
    SfHeader,
    PageTrackingGtm,
  },
  props: {
    categoryId: {
      type: String,
      required: true,
      default() {
        return '';
      },
    },
    searchSpecsStr: {
      type: String,
      required: true,
      default() {
        return '';
      },
    },
  },
  setup(props) {
    const id = props.categoryId;
    const { menu, search: searchMenu } = useMenu(`cat-${id}`);
    const { result: categorySearchResult, search: categorySearch } = useCategorySearch(`categorySearch:${id}`);
    const { klaviyoPagePush } = useKlaviyo();

    const breadcrumbsBase = 'Shop';
    const breadcrumbs = ref();

    useFetch(async () => {
      await searchMenu({ code: 'menu-plp' });
      await categorySearch({
        filters: {
          category_uid: {
            eq: id,
          },
        },
      });
      if (categorySearchResult.value.length > 0) {
        const categoryName = categorySearchResult.value[0]?.name;
        breadcrumbs.value = `${breadcrumbsBase} > ${categoryName}`;
        if (typeof window !== 'undefined' && categoryName) {
          await klaviyoPagePush(`PLP - ${categoryName}`);
        }
      }
    });

    const pageTitleGtm = computed(
      () => categorySearchResult?.value[0]?.meta_title,
    );
    const metaDescription = computed(() => {
      const meta = categorySearchResult?.value[0]?.meta_description;
      return meta?.replace(/(<([^>]+)>)/gi, '');
    });

    return {
      menu,
      breadcrumbs,
      categorySearchResult,
      metaDescription,
      pageTitleGtm,
    };
  },
  head() {
    return {
      title: this?.categorySearchResult?.[0]?.meta_title,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: this?.metaDescription,
        },
        {
          hid: 'keywords',
          name: 'keywords',
          content: this?.categorySearchResult?.[0]?.meta_keywords,
        },
        {
          hid: 'og:title',
          property: 'og:title',
          content: this?.categorySearchResult?.[0]?.meta_title,
        },
        {
          hid: 'og:description',
          property: 'og:description',
          content: this?.metaDescription,
        },
      ],
    };
  },
});
</script>

<style lang="scss" scoped>
.vue-content-placeholders {
  @include for-mobile {
    padding: 23px 0;
  }
  .vue-content-placeholders-img {
    height: 50px;
    @include for-desktop {
      margin-top: 75px;
    }
  }
}
</style>

<style lang="scss">
#category {
  .sf-loader {
    height: 100%;
    &__overlay {
      position: relative;
      height: 151px;
      background-color: #f6f3ef;
      @include for-desktop {
        height: 250px;
      }
    }
  }
  .sf-header__title {
    display: none;
  }
}

.category-top-container {
  background: #f6f3ef;
  padding-top: 30px;

  .category-top {
    margin: 0 auto;
    max-width: 1246px;
    padding-top: 85px;
    .breadcrumb-container {
      margin-top: 75px;
    }

    .sf-header__navigation {
      margin-left: 0;
    }

    .sf-header-navigation-item {
      flex: unset;
    }
  }
}

.navigation {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  max-width: 1246px;
  width: 100vw;
  padding: 0;

  .sf-header__wrapper {
    background: inherit;

    .sf-header__actions {
      display: block;

      .sf-header__navigation {
        flex-wrap: wrap;
        justify-content: center;
      }
    }
  }
  .nuxt-link-exact-active {
    color: #d28571;
    &::before {
      color: #222a24;
    }
  }

  .sf-search-bar,
  .sf-header__icons {
    display: none;
  }

  .nav-item {
    font-family: 'Frank Ruhl Libre';
    font-size: 20px;
    font-weight: 400;
    letter-spacing: 0px;
    line-height: 38px;
    position: relative;
    text-align: center;
    &:hover .sf-link {
      border: none !important;
    }
    .sf-link {
      font-family: 'Frank Ruhl Libre';
      font-size: 20px;
      font-weight: 400;
      letter-spacing: 0px;
      line-height: 38px;
      margin-bottom: 0;
      padding: 0 20px;
      position: relative;
      text-align: center;
      text-transform: capitalize;
      width: 100%;
      border: none;
    }

    &:before {
      content: '|';
      display: inline-block;
      left: 0;
      line-height: 38px;
      position: absolute;
    }

    &:first-child:before {
      content: '';
    }
  }
}

.breadcrumb-container {
  display: flex;
  justify-content: space-between;
  .breadcrumb {
    align-items: center;
    color: #222a24;
    display: flex;
    flex-wrap: wrap;
    font-family: 'Spartan';
    font-size: 16px;
    font-weight: 500;
    line-height: 23px;
    span {
      font-size: inherit;
      font-weight: inherit;
    }
  }
  h4 {
    font-family: inherit;
  }
  .crumb {
    padding: 0 20px;
    position: relative;
    &:before {
      content: '>';
      display: block;
      left: 5px;
      position: absolute;
      top: 55%;
      transform: translateY(-50%);
    }
  }
  .breadcrumb-home {
    padding-right: 20px;
    position: relative;
    &:after {
      content: '>';
      display: block;
      position: absolute;
      right: 5px;
      top: 55%;
      transform: translateY(-50%);
    }

    .crumb {
      padding: 0 20px;
      position: relative;

      &:before {
        position: absolute;
        left: 5px;
        content: '>';
        display: block;
      }
    }
  }
}
@media (max-width: 1023px) {
  .navigation {
    display: none;
  }

  .breadcrumb-container {
    .breadcrumb {
      color: #000;
      font-size: 12px;
      line-height: 18px;
      .breadcrumb-title {
        text-decoration: underline;
      }
    }
  }

  .category-top-container {
    margin-top: 0;
    padding: 0 24px;
    .category-top {
      padding-top: 0;
      .breadcrumb-container {
        margin-top: 0;
        flex-direction: column;
        .breadcrumb {
          display: none;
          margin-top: 23px;
        }
        .category-filter {
          background: white;
          margin: 23px 0;
          padding: 15px 25px;
          position: relative;
        }
      }
    }
  }
  .products {
    padding: 0 24px;
  }
}
</style>
